import { createFileRoute, redirect } from '@tanstack/react-router'
import { useAuthStore } from '@/stores/auth'
import { PendingApprovalPage } from '@/features/auth/PendingApprovalPage'

/**
 * Public route that explains the post-registration approval gate.
 *
 *   `/pending-approval?email=foo@bar.com&from=login`
 *
 *   - `email` (optional): echoed in the card so the user knows which
 *     account is pending.
 *   - `from` (optional): when "login", the headline switches from "you
 *     just registered" to "you're still waiting" — covers the case
 *     where a user with a pending account tries to log in.
 *
 * An already-authenticated user has nothing useful to see here, so we
 * bounce them to /dashboard from beforeLoad. The parent /login and
 * /register routes do the same — same pattern.
 */
type PendingSearch = {
  email?: string
  from?: 'login' | 'register'
}

export const Route = createFileRoute('/pending-approval')({
  beforeLoad: () => {
    if (useAuthStore.getState().isAuthenticated) {
      throw redirect({ to: '/dashboard' })
    }
  },
  validateSearch: (search: Record<string, unknown>): PendingSearch => {
    const out: PendingSearch = {}
    if (typeof search.email === 'string' && search.email.length > 0) {
      out.email = search.email
    }
    if (search.from === 'login' || search.from === 'register') {
      out.from = search.from
    }
    return out
  },
  component: PendingApprovalRouteComponent,
})

function PendingApprovalRouteComponent() {
  const { email, from } = Route.useSearch()
  return <PendingApprovalPage email={email} fromLogin={from === 'login'} />
}
